import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import Cookies from "js-cookie";
import { motion } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import { Navbar } from "./Navbar.jsx";
import { ClipLoader } from "react-spinners";

const api = process.env.REACT_APP_API_URL;

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

/** Creates a guest session if the user isn't logged in, stores token in js-cookie. */
async function ensureAuth() {
  if (Cookies.get("access_token")) return true;
  try {
    const { data } = await axios.post(`${api}auth/guest-session/`, {}, { withCredentials: true });
    Cookies.set("access_token", data.access, { expires: 1 });
    const userDetails = data.user || { first_name: "Guest", last_name: "", email: "", is_guest: true };
    localStorage.setItem("userdetails", JSON.stringify(userDetails));
    return true;
  } catch {
    toast.error("Could not start a session. Please try again.");
    return false;
  }
}

export default function FileUpload() {
  const navigate = useNavigate();

  const [step,               setStep]               = useState(1); // 1 = resume, 2 = JD
  const [resumeFile,         setResumeFile]         = useState(null);
  const [jdFile,             setJdFile]             = useState(null);
  const [jdText,             setJdText]             = useState("");
  const [jdTextValid,        setJdTextValid]        = useState(false);
  const [loader,             setLoader]             = useState(false);

  // ── Step 1: resume dropzone ────────────────────────────────────────────────
  const onDropResume = useCallback(async (accepted, rejected) => {
    if (rejected.length > 0) {
      toast.error("Unsupported file type or file too large (max 10 MB).");
      return;
    }
    const file = accepted[0];
    if (!file) return;

    const ok = await ensureAuth();
    if (!ok) return;

    setResumeFile(file);
    toast.success(`Resume uploaded: ${file.name}`);
    setTimeout(() => setStep(2), 800);
  }, []);

  const { getRootProps: getResumeProps, getInputProps: getResumeInput, isDragActive: resumeDrag } =
    useDropzone({
      onDrop: onDropResume,
      accept: { "application/pdf": [".pdf"], "application/msword": [".doc"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] },
      maxSize: MAX_SIZE,
      multiple: false,
    });

  // ── Step 2: JD dropzone ────────────────────────────────────────────────────
  const onDropJD = useCallback((accepted, rejected) => {
    if (rejected.length > 0) {
      toast.error("Unsupported file type or file too large (max 10 MB).");
      return;
    }
    const file = accepted[0];
    if (!file) return;
    setJdFile(file);
    setJdText("");
    setJdTextValid(true);
    toast.success(`JD uploaded: ${file.name}`);
  }, []);

  const { getRootProps: getJDProps, getInputProps: getJDInput, isDragActive: jdDrag } =
    useDropzone({
      onDrop: onDropJD,
      accept: { "application/pdf": [".pdf"], "application/msword": [".doc"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] },
      maxSize: MAX_SIZE,
      multiple: false,
    });

  const handleJdText = (e) => {
    const val = e.target.value;
    setJdText(val);
    setJdFile(null);
    setJdTextValid(val.trim().length >= 100);
    if (val.trim().length > 0 && val.trim().length < 100) {
      // show char count hint (no toast spam, just state)
    }
  };

  const canProceed = !!resumeFile && (!!jdFile || jdTextValid);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canProceed) return;

    const token = Cookies.get("access_token");
    if (!token) {
      const ok = await ensureAuth();
      if (!ok) return;
    }

    const formData = new FormData();
    formData.append("resume_file", resumeFile);

    if (jdFile) {
      formData.append("jd_file", jdFile);
    } else {
      const blob = new Blob([jdText], { type: "text/plain" });
      formData.append("jd_file", new File([blob], "job_description.txt"));
    }

    setLoader(true);
    try {
      const { data, status } = await axios.post(`${api}analysis/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Cookies.get("access_token")}`,
        },
        withCredentials: true,
      });

      if (status === 200) {
        // Persist session id immediately so every later step (including the
        // express path that skips the score page) can send X-Session-Id.
        if (data?.session_id) {
          localStorage.setItem("sessionId", data.session_id);
        }
        const steps = JSON.parse(localStorage.getItem("completedSteps") || "[]");
        if (!steps.includes("upload")) {
          steps.push("upload");
          localStorage.setItem("completedSteps", JSON.stringify(steps));
        }
        toast.success("Files analysed successfully!");
        navigate("/filesPreview", { state: { data, resumeFile, jobDescriptionFile: jdFile, jobDescriptionText: jdText } });
      }
    } catch {
      toast.error("Failed to analyse files. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loader) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100 gap-4">
        <ClipLoader color="#2c5141" size={52} />
        <p className="text-gray-600 font-medium">Analysing your resume…</p>
        <p className="text-gray-400 text-sm">This may take up to 30 seconds</p>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white pt-10 pb-16 px-4">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <button onClick={() => navigate("/")} className="text-sm text-gray-500 hover:text-gray-700 transition">
              ← Home
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              {step === 1 ? "Upload Your Resume" : "Upload Job Description"}
            </h1>
            <button
              onClick={handleSubmit}
              disabled={!canProceed}
              className="px-5 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Analyse →
            </button>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-3 mb-8 justify-center">
            {[{ n: 1, label: "Resume" }, { n: 2, label: "Job Description" }].map(({ n, label }) => (
              <React.Fragment key={n}>
                <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  step === n ? "bg-blue-600 text-white" : n < step ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                }`}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold bg-white/30">
                    {n < step ? "✓" : n}
                  </span>
                  {label}
                </div>
                {n < 2 && <div className="w-8 h-px bg-gray-300" />}
              </React.Fragment>
            ))}
          </div>

          {/* ── Step 1: Resume dropzone ── */}
          {step === 1 && (
            <div
              {...getResumeProps()}
              className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                resumeDrag
                  ? "border-blue-500 bg-blue-50"
                  : resumeFile
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50"
              }`}
            >
              <input {...getResumeInput()} />
              <div className="text-5xl mb-4">{resumeFile ? "✅" : "📄"}</div>
              {resumeFile ? (
                <p className="text-green-700 font-semibold">{resumeFile.name}</p>
              ) : (
                <>
                  <p className="text-lg font-semibold text-gray-700 mb-1">
                    {resumeDrag ? "Drop it here!" : "Drag & drop your resume"}
                  </p>
                  <p className="text-gray-400 text-sm">or click to browse · PDF, DOC, DOCX · max 10 MB</p>
                </>
              )}
            </div>
          )}

          {/* ── Step 2: JD dropzone + text ── */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              {/* JD file dropzone */}
              <div
                {...getJDProps()}
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all mb-6 ${
                  jdDrag
                    ? "border-blue-500 bg-blue-50"
                    : jdFile
                    ? "border-green-400 bg-green-50"
                    : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50"
                }`}
              >
                <input {...getJDInput()} />
                <div className="text-4xl mb-3">{jdFile ? "✅" : "📋"}</div>
                {jdFile ? (
                  <p className="text-green-700 font-semibold">{jdFile.name}</p>
                ) : (
                  <>
                    <p className="text-base font-semibold text-gray-700 mb-1">
                      {jdDrag ? "Drop it here!" : "Drag & drop job description"}
                    </p>
                    <p className="text-gray-400 text-sm">PDF, DOC, DOCX · max 10 MB</p>
                  </>
                )}
              </div>

              {/* OR divider */}
              <div className="flex items-center gap-4 mb-6">
                <hr className="flex-1 border-gray-200" />
                <span className="text-gray-400 text-sm font-medium">OR paste it below</span>
                <hr className="flex-1 border-gray-200" />
              </div>

              {/* JD text area */}
              <div className="relative">
                <textarea
                  className={`w-full h-48 rounded-2xl border p-4 resize-none text-sm focus:outline-none focus:ring-2 transition ${
                    jdText && !jdTextValid
                      ? "border-orange-300 focus:ring-orange-300 bg-orange-50"
                      : jdTextValid
                      ? "border-green-400 focus:ring-green-300 bg-green-50"
                      : "border-gray-300 focus:ring-blue-400 bg-white"
                  }`}
                  placeholder="Paste the job description here (minimum 100 characters)…"
                  value={jdText}
                  onChange={handleJdText}
                  disabled={!!jdFile}
                />
                <div className={`absolute bottom-3 right-4 text-xs font-medium transition ${
                  jdTextValid ? "text-green-600" : jdText ? "text-orange-500" : "text-gray-400"
                }`}>
                  {jdText.trim().length} / 100 min
                </div>
              </div>
              {jdText && !jdTextValid && (
                <p className="text-orange-500 text-xs mt-1.5 ml-1">
                  Job description must be at least 100 characters.
                </p>
              )}

              {/* Back to step 1 */}
              <button
                onClick={() => setStep(1)}
                className="mt-4 text-sm text-gray-400 hover:text-gray-600 transition"
              >
                ← Change resume
              </button>
            </motion.div>
          )}

          {/* Analyse button at bottom (mobile-friendly) */}
          {step === 2 && (
            <div className="mt-8 text-center">
              <button
                onClick={handleSubmit}
                disabled={!canProceed}
                className="px-10 py-3 rounded-xl bg-blue-600 text-white font-semibold text-lg hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-md"
              >
                Analyse My Resume
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
